import { NgModule, inject } from '@angular/core';
import { CanActivateFn, Router, Routes, RouterModule } from '@angular/router';
import { HomeComponent } from './pages/home/home.component';
import { SignInPageComponent } from './pages/sign-in-page/sign-in-page.component';
import { SignInEmailStepComponent } from './pages/sign-in-page/email-step/sign-in-email-step.component';
import { SignInPasswordStepComponent } from './pages/sign-in-page/password-step/sign-in-password-step.component';
import { SignInOtpChannelStepComponent } from './pages/sign-in-page/otp-channel-step/sign-in-otp-channel-step.component';
import { SignInOtpEntryStepComponent } from './pages/sign-in-page/otp-entry-step/sign-in-otp-entry-step.component';
import { RegistrationPageComponent } from './pages/registration-page/registration-page.component';
import { InvalidEmailPageComponent } from './pages/invalid-email-page/invalid-email-page.component';
import { SignInRedirectorComponent } from './pages/sign-in-redirector/sign-in-redirector.component';
import { ErrorPageComponent } from './pages/error-page/error-page.component';
import { SignOutPageComponent } from './pages/sign-out-page/sign-out-page.component';
import { ForgotPasswordPageComponent } from './pages/forgot-password-page/forgot-password-page.component';
import { PageGreeting } from './models/page-greeting.model';
import { SignInService } from './services/sign-in-service';

// Every page shares the sign-in look: the light background and the flow's greeting (see
// PageGreeting). Children inherit this data from the componentless parent and override `greeting`.
// KEEP IN SYNC with the pre-boot shell in src/index.html.
const signInGreeting: PageGreeting = { dark: 'Welcome,', gold: "let's sign you in." };
const registerGreeting: PageGreeting = {
  dark: 'Welcome,',
  gold: "let's get you registered.",
  subheader: 'Your financial future, at your fingertips.',
  paragraph: "With one click you will have a bird's-eye view of your investments, making it easier, simpler, and faster.",
};
const signOutGreeting: PageGreeting = { dark: 'Please wait,', gold: "we're signing you out." };

/**
 * Opens a page only when sign-in's earlier steps have collected what it needs; otherwise sign-in
 * starts again from its first step. That data is in memory only, so a refresh starts again too.
 */
const afterSignInStep = (ready: (signIn: SignInService) => boolean): CanActivateFn =>
  () => ready(inject(SignInService)) || inject(Router).createUrlTree(['/sign-in']);

const routes: Routes = [
  { path: '', data: { colorScheme: { background: 'light', sections: 'white', controls: 'light' }, greeting: signInGreeting }, children: [
    { path: 'sign-in-redirect', component: SignInRedirectorComponent },
    // `data.step` is the step's place in the shell's step indicator.
    { path: 'sign-in', component: SignInPageComponent, data: { controlStyle: 'squared' }, children: [
      { path: '', pathMatch: 'full', component: SignInEmailStepComponent, data: { step: 0 } },
      { path: 'password', component: SignInPasswordStepComponent, data: { step: 1 },
        canActivate: [afterSignInStep(signIn => !!signIn.account())] },
      { path: 'otp-channel', component: SignInOtpChannelStepComponent, data: { step: 2 },
        canActivate: [afterSignInStep(signIn => !!signIn.account() && !!signIn.password())] },
      { path: 'otp', component: SignInOtpEntryStepComponent, data: { step: 2 },
        canActivate: [afterSignInStep(signIn => signIn.otpSent())] },
    ] },
    { path: 'sign-out', component: SignOutPageComponent, data: { greeting: signOutGreeting } },
    { path: 'signin-oidc', component: SignOutPageComponent, data: { greeting: signOutGreeting } },
    { path: 'register', component: RegistrationPageComponent, data: { controlStyle: 'squared', greeting: registerGreeting } },
    { path: 'forgot-password', component: ForgotPasswordPageComponent, data: { controlStyle: 'squared', greeting: null },
      canActivate: [afterSignInStep(signIn => !!signIn.passwordResetRequestId())] },
    { path: 'invalid-email', component: InvalidEmailPageComponent },
    { path: 'auth-error', component: ErrorPageComponent },
    // IdentityServer's UserInteraction.ErrorUrl (Startup.cs)
    { path: 'error', component: ErrorPageComponent },
    {
      path: 'account', children: [
        { path: 'login', component: SignInRedirectorComponent },
      ]
    },
    { path: '', component: HomeComponent, pathMatch: 'full' },
    // Server-side errors are re-executed into the SPA at the original URL (e.g. /external/callback)
    { path: '**', component: ErrorPageComponent },
  ] },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { enableTracing: false })],
  exports: [RouterModule],
})
export class AppRoutingModule { }
